import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Slide,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { indicadores } from '../utils/variables';
import WidgetsIcon from '@mui/icons-material/Widgets';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

export const DEFAULT_CONFIG = {
  tipoIndicador: 'uptime',
  oppositeAxis: false,
  colorIndicador: {
    uptime: {
      maxColor: indicadores.uptime.axisConfig.maxColor,
      minColor: indicadores.uptime.axisConfig.minColor,
    },
    downtime: {
      maxColor: indicadores.downtime.axisConfig.maxColor,
      minColor: indicadores.downtime.axisConfig.minColor,
    },
  },
};

export function ConfigTools({ heatmapSettings, setHeatmapSettings }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [minColor, setMinColor] = React.useState(
    heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador].minColor
  );
  const [maxColor, setMaxColor] = React.useState(
    heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador].maxColor
  );

  React.useEffect(() => {
    const handler = setTimeout(() => {
      handleChangeColorIndicador('min', minColor);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [minColor]);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      handleChangeColorIndicador('max', maxColor);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [maxColor]);

  function handleChangeIndicador(event) {
    setHeatmapSettings((prevState) => ({
      ...prevState,
      tipoIndicador: event.target.value,
    }));
  }

  function handleChangeColorIndicador(type, color) {
    setHeatmapSettings((prevState) => ({
      ...prevState,
      colorIndicador: {
        ...prevState.colorIndicador,
        [heatmapSettings.tipoIndicador]: {
          ...prevState.colorIndicador[heatmapSettings.tipoIndicador],
          [`${type}Color`]: color,
        },
      },
    }));
  }

  function handleChangeOppositeAxis(event) {
    setHeatmapSettings((prevState) => ({
      ...prevState,
      oppositeAxis: event.target.value === 'true',
    }));
  }

  function handleResetSettings() {
    setHeatmapSettings(DEFAULT_CONFIG);
    setMinColor(
      DEFAULT_CONFIG.colorIndicador[heatmapSettings.tipoIndicador].minColor
    );
    setMaxColor(
      DEFAULT_CONFIG.colorIndicador[heatmapSettings.tipoIndicador].maxColor
    );
  }

  function handleMinColorChange(e) {
    setMinColor(e.target.value);
  }

  function handleMaxColorChange(e) {
    setMaxColor(e.target.value);
  }

  return (
    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      flexDirection="row"
    >
      <Box>
        <Tooltip title={(t && typeof t === 'function') ? (t('heatmap.config.button.tooltip') || 'Configuración mapa de calor') : 'Configuración mapa de calor'}>
          <Button
            onClick={() => setOpen(!open)}
            color={open ? 'primary' : 'default'}
          >
            <WidgetsIcon />
            {!open && <KeyboardArrowRightIcon />}
            {open && <KeyboardArrowLeftIcon />}
          </Button>
        </Tooltip>
      </Box>
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
        <Box
          style={{
            display: 'flex',
            padding: 10,
            borderColor: '#c4c4c4',
            borderRadius: 5,
            marginRight: 20,
            overflow: 'auto',
          }}
        >
          <Box
            style={{
              borderRightStyle: 'solid',
              borderRightWidth: 1,
              borderRightColor: '#c4c4c4',
            }}
          >
            <FormControl>
              <FormLabel id="indicador-radio">{ (t && typeof t === 'function') ? (t('heatmap.config.radio.indicator.label') || 'Indicador') : 'Indicador' }</FormLabel>
              <RadioGroup
                row
                aria-labelledby="indicador-radio"
                name="radio-indicador"
                onChange={handleChangeIndicador}
                value={heatmapSettings.tipoIndicador}
              >
                <FormControlLabel
                  value="uptime"
                  control={<Radio color="primary" />}
                  label={(t && typeof t === 'function') ? (t('objective.status.uptime') || 'Uptime') : 'Uptime'}
                />
                <FormControlLabel
                  value="downtime"
                  control={<Radio color="primary" />}
                  label={(t && typeof t === 'function') ? (t('objective.status.downtime') || 'Downtime') : 'Downtime'}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box
            style={{
              borderRightStyle: 'solid',
              borderRightWidth: 1,
              borderRightColor: '#c4c4c4',
              paddingLeft: 10,
            }}
          >
            <FormControl>
              <FormLabel id="opposite-axis-radio">{ (t && typeof t === 'function') ? (t('heatmap.config.radio.x-axis-position.label') || 'Posición eje horas') : 'Posición eje horas' }</FormLabel>
              <RadioGroup
                row
                aria-labelledby="opposite-axis-radio"
                name="radio-opposite-axis"
                onChange={handleChangeOppositeAxis}
                value={heatmapSettings.oppositeAxis ? 'true' : 'false'}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio color="primary" />}
                  label={(t && typeof t === 'function') ? (t('heatmap.config.radio.x-axis-position.option.bottom') || 'Abajo') : 'Abajo'}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio color="primary" />}
                  label={(t && typeof t === 'function') ? (t('heatmap.config.radio.x-axis-position.option.top') || 'Arriba') : 'Arriba'}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box
            style={{
              borderRightStyle: 'solid',
              borderRightWidth: 1,
              borderRightColor: '#c4c4c4',
              paddingLeft: 10,
              paddingRight: 10,
            }}
          >
            <Typography>{ (t && typeof t === 'function') ? (t('heatmap.config.input-color.label.min-color') || 'Color mínimo:') : 'Color mínimo:' }</Typography>
            <Box display="flex" justifyContent="center">
              <input
                type="color"
                onChange={(e) => handleMinColorChange(e)}
                value={
                  heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador]
                    .minColor
                }
                style={{
                  justifySelf: 'center',
                  marginTop: 5,
                  marginBottom: 5,
                }}
              />
            </Box>
          </Box>
          <Box style={{ paddingLeft: 10 }}>
            <Typography>{ (t && typeof t === 'function') ? (t('heatmap.config.input-color.label.max-color') || 'Color máximo:') : 'Color máximo:' }</Typography>
            <Box display="flex" justifyContent="center">
              <input
                type="color"
                onChange={(e) => handleMaxColorChange(e)}
                value={
                  heatmapSettings.colorIndicador[heatmapSettings.tipoIndicador]
                    .maxColor
                }
                style={{
                  justifySelf: 'center',
                  marginTop: 5,
                  marginBottom: 5,
                }}
              />
            </Box>
          </Box>
          <Box marginLeft={2} display="flex" alignItems="center">
            <Tooltip title={(t && typeof t === 'function') ? (t('heatmap.config.reset-config') || 'Reestablecer configuración') : 'Reestablecer configuración'}>
              <IconButton onClick={handleResetSettings}>
                <SettingsBackupRestoreIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
}
